/**
 * Skills Graph wrapper — round-trips Anthropic `SKILL.md` files between
 * the firm's `Firm-Skills` SharePoint site and Repancy. Convex is the
 * runtime source of truth (decision D3); Graph is hit only on
 * Sync / Save / Open user actions.
 *
 * Parser: unified + remark-parse + remark-frontmatter + yaml. Chosen
 * over gray-matter because we want extensibility (rendering, AST walks,
 * future link/citation lints) without bringing in a separate Markdown
 * stack later.
 *
 * Audit: every write logs via the AuditWriter interface (leaf-modules
 * memory). Reads do not audit; Layer-2 tests assert this so we don't
 * spam the audit log on picker hover.
 */
import type { DriveItem } from '@microsoft/microsoft-graph-types';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkFrontmatter from 'remark-frontmatter';
import { parse as parseYaml, stringify as stringifyYaml } from 'yaml';
import {
  withRetry,
  READ_POLICY,
  WRITE_POLICY,
} from '@repancy/graph-core';
import { getGraphClient } from './graphClient';
import type { GraphAuditOp } from './audit';

// ── Frontmatter spec (Anthropic open standard) ───────────────────────
// name:        <=64 chars, lowercase + digits + hyphens, no XML tags,
//              no reserved words 'anthropic' | 'claude'.
// description: non-empty, <=1024 chars, no XML tags.
// extra:       arbitrary keys preserved on round-trip.

export interface SkillFrontmatter {
  name: string;
  description: string;
  /** Arbitrary additional keys, preserved on round-trip. */
  extra?: Record<string, unknown>;
}

export interface SkillFrontmatterError {
  field: 'name' | 'description' | 'frontmatter';
  reason: string;
}

const NAME_PATTERN = /^[a-z0-9][a-z0-9-]*$/;
const RESERVED_WORDS = new Set(['anthropic', 'claude']);
const XML_TAG_PATTERN = /<\/?[a-z][\s\S]*?>/i;

export function validateFrontmatter(
  fm: unknown,
): SkillFrontmatter | SkillFrontmatterError[] {
  const errors: SkillFrontmatterError[] = [];
  if (fm === null || typeof fm !== 'object') {
    return [{ field: 'frontmatter', reason: 'frontmatter must be a YAML mapping' }];
  }
  const obj = fm as Record<string, unknown>;
  const name = obj.name;
  const description = obj.description;

  if (typeof name !== 'string' || name.length === 0) {
    errors.push({ field: 'name', reason: 'name is required and must be a non-empty string' });
  } else {
    if (name.length > 64) errors.push({ field: 'name', reason: 'name must be <=64 characters' });
    if (!NAME_PATTERN.test(name)) {
      errors.push({
        field: 'name',
        reason: 'name must be lowercase letters, digits, and hyphens only, and start with a letter or digit',
      });
    }
    if (XML_TAG_PATTERN.test(name)) {
      errors.push({ field: 'name', reason: 'name must not contain XML tags' });
    }
    for (const w of RESERVED_WORDS) {
      if (name.includes(w)) {
        errors.push({ field: 'name', reason: `name must not contain the reserved word "${w}"` });
      }
    }
  }

  if (typeof description !== 'string' || description.trim().length === 0) {
    errors.push({
      field: 'description',
      reason: 'description is required and must be a non-empty string',
    });
  } else {
    if (description.length > 1024) {
      errors.push({ field: 'description', reason: 'description must be <=1024 characters' });
    }
    if (XML_TAG_PATTERN.test(description)) {
      errors.push({ field: 'description', reason: 'description must not contain XML tags' });
    }
  }

  if (errors.length > 0) return errors;

  const extra: Record<string, unknown> = {};
  for (const [k, value] of Object.entries(obj)) {
    if (k === 'name' || k === 'description') continue;
    extra[k] = value;
  }

  const result: SkillFrontmatter = {
    name: name as string,
    description: description as string,
  };
  if (Object.keys(extra).length > 0) result.extra = extra;
  return result;
}

// ── SKILL.md parse / render ──────────────────────────────────────────

const parser = unified().use(remarkParse).use(remarkFrontmatter, ['yaml']);

export interface ParsedSkill {
  frontmatter: SkillFrontmatter;
  body: string;
  /** Raw text exactly as provided (parser is non-destructive for round-trips). */
  raw: string;
}

/**
 * Parse a SKILL.md text. Returns either the parsed skill or a list of
 * validation errors; never throws on bad input.
 */
export function parseSkillMd(text: string): ParsedSkill | SkillFrontmatterError[] {
  const tree = parser.parse(text);
  const frontmatterNode = tree.children[0];
  if (!frontmatterNode || frontmatterNode.type !== 'yaml') {
    return [{ field: 'frontmatter', reason: 'SKILL.md must start with a YAML frontmatter block' }];
  }
  const rawYaml = (frontmatterNode as { value: string }).value;
  let parsedYaml: unknown;
  try {
    parsedYaml = parseYaml(rawYaml);
  } catch (err) {
    return [{
      field: 'frontmatter',
      reason: `YAML frontmatter could not be parsed: ${(err as Error).message}`,
    }];
  }
  const fmOrErrors = validateFrontmatter(parsedYaml);
  if (Array.isArray(fmOrErrors)) return fmOrErrors;
  const body = extractBody(text);
  return { frontmatter: fmOrErrors, body, raw: text };
}

/** Extract everything after the closing `---` line. */
function extractBody(text: string): string {
  const m = text.match(/^---\n[\s\S]*?\n---\n?/);
  return m ? text.slice(m[0].length) : text;
}

/** Render a SkillFrontmatter + body into SKILL.md text. */
export function renderSkillMd(fm: SkillFrontmatter, body: string): string {
  const fmObj: Record<string, unknown> = {
    name: fm.name,
    description: fm.description,
    ...(fm.extra ?? {}),
  };
  const yamlText = stringifyYaml(fmObj).trimEnd();
  const sep = body.startsWith('\n') ? '' : '\n';
  return `---\n${yamlText}\n---${sep}${body}`;
}

// ── Manifest hashing ─────────────────────────────────────────────────

export interface SkillManifestEntry {
  path: string;
  kind: 'script' | 'reference' | 'asset' | 'other';
  sha256: string;
}

/**
 * Cheap and stable hash for change detection. NOT a cryptographic
 * boundary — only used to short-circuit re-uploads.
 *
 * Uses SubtleCrypto in the browser; falls back to a tiny FNV-1a hex
 * encoding in Node test environments where crypto.subtle may be
 * unavailable for synchronous calls.
 */
export async function hashContent(bytes: Uint8Array): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    // Clone into a fresh ArrayBuffer so the BufferSource type is exact
    // (Uint8Array<ArrayBufferLike> rejects under strict TS for digest).
    const copy = new Uint8Array(bytes.length);
    copy.set(bytes);
    const buf = await crypto.subtle.digest('SHA-256', copy.buffer);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // FNV-1a 64-bit, expressed as hex. Stable across runs, not crypto-safe.
  let h1 = 0xcbf29ce4;
  let h2 = 0x84222325;
  for (const byte of bytes) {
    h2 ^= byte;
    const lo1 = (h1 & 0xffff) * 0x01b3;
    const hi1 = (h1 >>> 16) * 0x01b3;
    h1 = (hi1 << 16) + lo1;
    h2 = Math.imul(h2, 0x01000193) >>> 0;
  }
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}

function classifyAsset(parentName: string): SkillManifestEntry['kind'] {
  switch (parentName) {
    case 'scripts': return 'script';
    case 'references': return 'reference';
    case 'assets': return 'asset';
    default: return 'other';
  }
}

// ── Remote types ─────────────────────────────────────────────────────

export interface RemoteSkill {
  /** SharePoint driveItem id of the SKILL.md file itself. */
  itemId: string;
  /** SharePoint driveItem id of the containing /Skills/<name>/ folder. */
  folderItemId: string;
  etag: string;
  webUrl: string;
  body: string;
  frontmatter: SkillFrontmatter;
  manifest: SkillManifestEntry[];
}

export interface AuditWriter {
  record(args: {
    op: GraphAuditOp;
    itemId: string;
    driveId: string;
    siteId: string;
    etagBefore?: string;
    etagAfter?: string;
    note?: string;
  }): Promise<void>;
}

// ── Graph operations ─────────────────────────────────────────────────

/**
 * Enumerate skill folders directly under `/Skills/` on the firm site.
 * For each folder, fetch SKILL.md and parse frontmatter. Skips folders
 * without a SKILL.md or with invalid frontmatter (logs reasons; never
 * throws — picker has to keep working even if one Skill is malformed).
 */
export async function listSkills(args: {
  siteId: string;
  driveId: string;
  root?: string;
}): Promise<{ remote: RemoteSkill[]; skipped: Array<{ folder: string; reasons: SkillFrontmatterError[] }> }> {
  const root = args.root ?? 'Skills';
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    const folders = await client
      .api(`/drives/${args.driveId}/root:/${root}:/children`)
      .get();
    const items: DriveItem[] = (folders.value ?? []).filter(
      (it: DriveItem) => !!it.folder,
    );
    const remote: RemoteSkill[] = [];
    const skipped: Array<{ folder: string; reasons: SkillFrontmatterError[] }> = [];
    for (const folder of items) {
      const folderItemId = folder.id;
      if (!folderItemId) continue;
      const folderName = folder.name ?? '(unnamed)';
      try {
        const skillItem = await client
          .api(`/drives/${args.driveId}/items/${folderItemId}:/SKILL.md`)
          .get();
        const itemId = skillItem.id as string;
        const etag = (skillItem.eTag as string | undefined) ?? '';
        const webUrl = (skillItem.webUrl as string | undefined) ?? '';
        const bodyText: string = await client
          .api(`/drives/${args.driveId}/items/${itemId}/content`)
          .responseType('text' as unknown as never)
          .get();
        const parsed = parseSkillMd(bodyText);
        if (Array.isArray(parsed)) {
          skipped.push({ folder: folderName, reasons: parsed });
          continue;
        }
        const manifest = await fetchManifest(args.driveId, folderItemId);
        remote.push({
          itemId,
          folderItemId,
          etag,
          webUrl,
          body: parsed.body,
          frontmatter: parsed.frontmatter,
          manifest,
        });
      } catch (err) {
        skipped.push({
          folder: folderName,
          reasons: [{ field: 'frontmatter', reason: `read failed: ${(err as Error).message}` }],
        });
      }
    }
    return { remote, skipped };
  });
}

/**
 * Walk `/Skills/<name>/{scripts,references,assets}/` and hash every
 * leaf file. Folders that don't exist are silently skipped.
 */
async function fetchManifest(
  driveId: string,
  folderItemId: string,
): Promise<SkillManifestEntry[]> {
  const client = getGraphClient();
  const subdirs = ['scripts', 'references', 'assets'];
  const out: SkillManifestEntry[] = [];
  for (const name of subdirs) {
    try {
      const children = await client
        .api(`/drives/${driveId}/items/${folderItemId}:/${name}:/children`)
        .get();
      for (const child of (children.value ?? []) as DriveItem[]) {
        const childId = child.id;
        const childName = child.name ?? '';
        if (!childId || !childName || child.folder) continue;
        const buf: ArrayBuffer = await client
          .api(`/drives/${driveId}/items/${childId}/content`)
          .responseType('arraybuffer' as unknown as never)
          .get();
        const sha256 = await hashContent(new Uint8Array(buf));
        out.push({ path: `${name}/${childName}`, kind: classifyAsset(name), sha256 });
      }
    } catch {
      // 404 etc. — subdir missing; skip silently.
    }
  }
  return out;
}

/**
 * Create or update `/Skills/<name>/SKILL.md`. Returns the resulting
 * RemoteSkill. Uses `If-Match` when an etag is supplied (412/409 are
 * conflict surfaces — fail fast, do not retry).
 */
export async function writeSkill(args: {
  siteId: string;
  driveId: string;
  root?: string;
  frontmatter: SkillFrontmatter;
  body: string;
  expectedEtag?: string;
  audit?: AuditWriter;
}): Promise<RemoteSkill> {
  const root = args.root ?? 'Skills';
  const fmOrErrors = validateFrontmatter(args.frontmatter);
  if (Array.isArray(fmOrErrors)) {
    throw new Error(
      `Invalid SKILL.md frontmatter: ${fmOrErrors.map((e) => `${e.field}: ${e.reason}`).join('; ')}`,
    );
  }
  const fm = fmOrErrors;
  const skillMd = renderSkillMd(fm, args.body);

  return withRetry(WRITE_POLICY, async () => {
    const client = getGraphClient();
    // Create the folder if absent (PATCH conflict-behavior=replace acts as upsert
    // when used as a Children POST; for explicit folder creation Graph accepts
    // a POST to /children with @microsoft.graph.conflictBehavior=replace).
    const folderPath = `/drives/${args.driveId}/root:/${root}/${fm.name}`;
    let folderItemId: string;
    try {
      const existing = await client.api(folderPath).get();
      folderItemId = existing.id as string;
    } catch {
      const created = await client
        .api(`/drives/${args.driveId}/root:/${root}:/children`)
        .post({
          name: fm.name,
          folder: {},
          '@microsoft.graph.conflictBehavior': 'replace',
        });
      folderItemId = (created.id ?? created.itemId) as string;
    }

    let request = client.api(`/drives/${args.driveId}/items/${folderItemId}:/SKILL.md:/content`);
    if (args.expectedEtag) {
      request = request.header('If-Match', args.expectedEtag);
    }
    const written = await request.put(skillMd);
    const itemId = written.id as string;
    const etag = (written.eTag as string | undefined) ?? '';
    const webUrl = (written.webUrl as string | undefined) ?? '';
    const manifest = await fetchManifest(args.driveId, folderItemId);

    if (args.audit) {
      await args.audit.record({
        op: args.expectedEtag ? 'skill-save' : 'skill-import',
        itemId,
        driveId: args.driveId,
        siteId: args.siteId,
        etagBefore: args.expectedEtag,
        etagAfter: etag,
        note: `skill:${fm.name}`,
      });
    }

    return {
      itemId,
      folderItemId,
      etag,
      webUrl,
      body: args.body,
      frontmatter: fm,
      manifest,
    };
  });
}

/**
 * Re-fetch a single skill by `(siteId, driveId, folderItemId)`. Used by
 * the "Refresh" affordance on a single library row when the manifest
 * appears stale.
 */
export async function refreshSkill(args: {
  driveId: string;
  folderItemId: string;
}): Promise<RemoteSkill | SkillFrontmatterError[]> {
  return withRetry(READ_POLICY, async () => {
    const client = getGraphClient();
    const skillItem = await client
      .api(`/drives/${args.driveId}/items/${args.folderItemId}:/SKILL.md`)
      .get();
    const itemId = skillItem.id as string;
    const etag = (skillItem.eTag as string | undefined) ?? '';
    const webUrl = (skillItem.webUrl as string | undefined) ?? '';
    const bodyText: string = await client
      .api(`/drives/${args.driveId}/items/${itemId}/content`)
      .responseType('text' as unknown as never)
      .get();
    const parsed = parseSkillMd(bodyText);
    if (Array.isArray(parsed)) return parsed;
    const manifest = await fetchManifest(args.driveId, args.folderItemId);
    return {
      itemId,
      folderItemId: args.folderItemId,
      etag,
      webUrl,
      body: parsed.body,
      frontmatter: parsed.frontmatter,
      manifest,
    };
  });
}
